import React, { useEffect, useState } from 'react';
import { Close } from "@mui/icons-material";
import api from '../../services/api';

interface AreaFormProps {
  area?: any;
  onClose: () => void;
  onSaved: () => void;
}

const TIPOS = [
  { value: 'municipio', label: 'Município' },
  { value: 'represa', label: 'Represa' },
  { value: 'bacia', label: 'Bacia' },
  { value: 'apa', label: 'APA' },
  { value: 'outro', label: 'Outro' },
];

const AreaForm: React.FC<AreaFormProps> = ({ area, onClose, onSaved }) => {
  const [nome, setNome] = useState(area?.nome || '');
  const [tipo, setTipo] = useState(area?.tipo || 'municipio');
  const [municipio, setMunicipio] = useState(area?.municipio || '');
  const [uf, setUf] = useState(area?.uf || '');
  const [projetos, setProjetos] = useState<any[]>([]);
  const [projetosSelecionados, setProjetosSelecionados] = useState<string[]>(
    area?.projetoVinculos?.map((v: any) => v.projeto?.uid_pesca_projeto).filter(Boolean) || []
  );
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api.get('/pesca/projetos').then(res => setProjetos(res.data.data));
  }, []);

  const toggleProjeto = (uid: string) => {
    setProjetosSelecionados(prev =>
      prev.includes(uid) ? prev.filter(p => p !== uid) : [...prev, uid]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) {
      setErro('Informe o nome da área.');
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      const payload = {
        nome: nome.trim(),
        tipo,
        municipio: municipio.trim() || null,
        uf: uf.trim() || null,
        uid_pesca_projetos: projetosSelecionados,
      };

      if (area?.uid_pesca_area) {
        await api.put(`/pesca/areas/${area.uid_pesca_area}`, payload);
      } else {
        await api.post('/pesca/areas', payload);
      }
      onSaved();
    } catch (err: any) {
      setErro(err?.response?.data?.message || 'Erro ao salvar área de pesca.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, color: 'var(--green-700, #00b37e)' }}>
            {area ? 'Editar Área de Pesca' : 'Nova Área de Pesca'}
          </h2>
          <Close style={{ cursor: 'pointer' }} onClick={onClose} />
        </div>

        <form onSubmit={handleSubmit}>
          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Nome *</label>
            <input style={inputStyle} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex: Médio Tietê" />
          </div>

          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Tipo *</label>
            <select style={inputStyle} value={tipo} onChange={(e) => setTipo(e.target.value)}>
              {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ ...fieldWrapStyle, flex: 1 }}>
              <label style={labelStyle}>Município</label>
              <input style={inputStyle} value={municipio} onChange={(e) => setMunicipio(e.target.value)} />
            </div>
            <div style={{ ...fieldWrapStyle, width: '80px' }}>
              <label style={labelStyle}>UF</label>
              <input style={inputStyle} value={uf} maxLength={2} onChange={(e) => setUf(e.target.value.toUpperCase())} />
            </div>
          </div>

          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Vincular a Projeto(s)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
              {projetos.map((p) => (
                <label key={p.uid_pesca_projeto} style={chipStyle(projetosSelecionados.includes(p.uid_pesca_projeto))}>
                  <input
                    type="checkbox"
                    style={{ marginRight: '6px' }}
                    checked={projetosSelecionados.includes(p.uid_pesca_projeto)}
                    onChange={() => toggleProjeto(p.uid_pesca_projeto)}
                  />
                  {p.nome}
                </label>
              ))}
              {projetos.length === 0 && <span style={{ color: '#999', fontSize: '0.85rem' }}>Nenhum projeto cadastrado.</span>}
            </div>
          </div>

          {erro && <p style={{ color: '#f75a68', fontSize: '0.85rem' }}>{erro}</p>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
            <button type="button" onClick={onClose} style={secondaryBtnStyle}>Cancelar</button>
            <button type="submit" disabled={salvando} style={primaryBtnStyle}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  background: '#fff', borderRadius: '12px', padding: '24px',
  width: '90%', maxWidth: '480px', boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
};

const fieldWrapStyle: React.CSSProperties = { marginBottom: '14px' };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '4px' };
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box',
};
const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: '20px', border: 'none', background: '#03c9fe', color: '#fff', cursor: 'pointer',
};
const secondaryBtnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: '20px', border: '1px solid #ddd', background: '#fff', color: '#666', cursor: 'pointer',
};
const chipStyle = (ativo: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', padding: '4px 10px', borderRadius: '16px',
  border: `1px solid ${ativo ? '#03c9fe' : '#ddd'}`, background: ativo ? '#e6f9ff' : '#fff',
  fontSize: '0.85rem', cursor: 'pointer',
});

export default AreaForm;
